import * as Location from 'expo-location';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Linking from 'expo-linking';
import { SentryPlatform } from 'utils/sentry';

import { types } from "../types";
import { isWeb } from "utils/platform";

let hasLocationWatcher = false;
let locationWatcher = null;

const getLocationForegroundPermissions = async() => {
    // On web, the browser will prompt when we actually call navigator.geolocation
    if (isWeb) {
        return "granted";
    }
    let { status } = await Location.requestForegroundPermissionsAsync();
    return status;
}

export const enableLocationWatcher = async(next) => {
    console.log('[Location] enableLocationWatcher called, hasLocationWatcher:', hasLocationWatcher);
    // there should only be one location watcher. If a new one is created, the old one can't be removed.
    if(!hasLocationWatcher) {
        hasLocationWatcher = true;
        let locationPreference = await getLocationPreference();
        console.log('[Location] Starting watcher, locationPreference:', locationPreference);

        if (locationPreference) {

            // check for permissions first
            const locationPermissionGranted = await getLocationForegroundPermissions();

            if(locationPermissionGranted === "denied") {
                return next({
                    type: types.USER_LOCATION_LOOKUP_FAIL
                })
            }

            if(locationPermissionGranted === "granted") {
                console.log('[Location] Starting position watch...');
                if (isWeb) {
                    locationWatcher = navigator.geolocation.watchPosition((res) => {
                        const {latitude, longitude, accuracy} = res.coords;
                        // console.log('[Location] Watch position update:', {latitude, longitude, accuracy});
                        const userPosition = {latitude, longitude};
                        return next({
                            type: types.USER_LOCATION_CHANGE,
                            userPosition,
                            accuracy
                        })
                    }, (err) => {
                        console.error('[Location] Watch position error:', err);
                        SentryPlatform.captureMessage(`ERROR watchPositionAsync WEB ${JSON.stringify(err)}`);
                        return next({
                            type: types.USER_LOCATION_LOOKUP_FAIL
                        })

                    }, {
                        enableHighAccuracy: false,
                        timeout: 20000,
                        maximumAge: 15000
                    });
                    console.log('[Location] Watch started with ID:', locationWatcher);
                } else {

                    locationWatcher = await Location.watchPositionAsync({
                        accuracy: Location.Accuracy.Balanced,
                        timeInterval: 10000,
                        distanceInterval: 15
                    }, (res) => {
                        const {latitude, longitude} = res.coords;
                        // console.log(`DID watchPositionAsync ${JSON.stringify({latitude, longitude})}`)
                        const userPosition = {latitude, longitude};
                        return next({
                            type: types.USER_LOCATION_CHANGE,
                            userPosition
                        });
                    });
                }
            }
        }

    }
}
const disableLocationWatcher = async () => {
    if (locationWatcher) {
        // console.log(`about to clearWatch`)
        if (isWeb) {
            navigator.geolocation.clearWatch(locationWatcher);
        } else {
            await locationWatcher.remove();
        }
        locationWatcher = null;
    }
    hasLocationWatcher = false;
}

const getLocationPreference = async() => {
    let locationPreference = await AsyncStorage.getItem('@locationPreference');
    if (locationPreference !== null) {
        // the value is saved as a string, this is forcing a boolean
        locationPreference = locationPreference === "true";
    }
    return locationPreference
}

export const userAPI = () => next => action => {

    const setLocationPreference = async(locationPreference) => {
        console.log('[Location] setLocationPreference called with:', locationPreference);
        await AsyncStorage.setItem('@locationPreference',`${locationPreference}`);
        if (locationPreference) {
            if (isWeb) {
                // On web, browser will ask for permission when we try to get location
                console.log('[Location] Web: skipping expo permission check, will prompt on geolocation call');
                userGetCurrentPosition();
            } else {
                // Native: request permission from expo-location
                console.log('[Location] Native: requesting permission...');
                const res = await Location.requestForegroundPermissionsAsync();
                const { status } = res;
                const isGranted = status === "granted";
                console.log('[Location] Permission result:', isGranted);
                await AsyncStorage.setItem('@locationPreference',`${isGranted}`);
                userGetCurrentPosition();
            }
        }

    }

    const userGetCurrentPosition = async() => {
        let locationPreference = await getLocationPreference();
        console.log('[Location] locationPreference:', locationPreference);
        if (locationPreference) {


            // check for permissions first
            const locationPermissionGranted = await getLocationForegroundPermissions();
            console.log('[Location] permissionGranted:', locationPermissionGranted);

            if(locationPermissionGranted === "denied") {
                console.log('[Location] Permission denied');
                return next({
                    type: types.USER_LOCATION_LOOKUP_FAIL
                })
            }

            if(locationPermissionGranted === "granted") {
                try {
                    console.log('[Location] Getting current position...');

                    if (isWeb) {
                        // Use native browser geolocation API for web
                        navigator.geolocation.getCurrentPosition((res) => {
                            const {latitude, longitude} = res.coords;
                            const userPosition = {latitude, longitude};
                            console.log('[Location] Got position (web):', userPosition);
                            return next({
                                type: types.USER_LOCATION_CHANGE,
                                userPosition
                            });
                        }, (error) => {
                            console.error('[Location] Error getting position (web):', error);
                            return next({
                                type: types.USER_LOCATION_LOOKUP_FAIL
                            });
                        }, {
                            enableHighAccuracy: false,
                            timeout: 10000,
                            maximumAge: 15000
                        });
                    } else {
                        const res = await Location.getCurrentPositionAsync({
                            accuracy: Location.Accuracy.Balanced
                        });

                        const {latitude, longitude} = res.coords;
                        const userPosition = {latitude, longitude};
                        console.log('[Location] Got position (native):', userPosition);
                        return next({
                            type: types.USER_LOCATION_CHANGE,
                            userPosition
                        });
                    }
                } catch (error) {
                    console.error('[Location] Error getting position:', error);
                    return next({
                        type: types.USER_LOCATION_LOOKUP_FAIL
                    });
                }
            }
        }
    }

    const checkLocationPreference = async() => {
        let locationPreference = await getLocationPreference();
        if (locationPreference) {
            await userGetCurrentPosition();
        }
        return next({
            type: types.USER_CHECKED_LOCATION_PREFERENCE,
            locationPreference
        });
    }

    const openURL = async(action) => {
        const { url } = action;

        if(Platform.OS == 'web'){
            window.open(url, '_blank');
        } else {
            Linking.openURL(url);
        }
    }

    next(action);
    switch (action.type) {
        case types.USER_SET_LOCATION_PREFERENCE: {
            setLocationPreference(action.locationPreference)
            break;
        }
        case types.USER_CHECK_LOCATION_PREFERENCE: {
            checkLocationPreference();
            break;
        }
        case types.SINGLE_ROUTE_LOOKUP_SUCCESS:
        case types.SET_WALKING_ROUTE_STOP:
        case types.SET_MAP_TO_POINT:
        {
            if (!action.offlineMode) {
                enableLocationWatcher(next);
            }
            break;
        }
        case types.EXIT_WALKING_ROUTE: {
            disableLocationWatcher();
            break;
        }
        case types.SHOW_DETAILS_PAGE:
        {
            if(action.detailsPageData) {
                disableLocationWatcher();
            } else {
                enableLocationWatcher(next);
            }
            break;
        }
        case types.SHOW_FOOTER_LIST:
        {
            if (action.showFooterList) {
                disableLocationWatcher();
            } else {
                enableLocationWatcher(next);
            }
            break;
        }
        case types.OPEN_URL: {
            openURL(action)
            break;
        }
        default:
            break;
    }
};